/**
 * The live connection to the world's room: hello and welcome, intents and their answers, presence,
 * pings for the clock, and reconnecting with a backoff. It patches the store only through game/sync.
 */
import {
  isGuest,
  parseServerMessage,
  PROTOCOL_VERSION,
  type ClientMessage,
} from '@bramvasterd/terrella-protocol';

import { logger } from '@core/log';
import { r4 } from '@core/math';
import { vec } from '@core/quat';
import { type Game } from '@game/game';
import { myHeading, myPoint } from '@game/player/local';
import {
  onEvent,
  onLeft,
  onMember,
  onOutdated,
  onPresence,
  onRefused,
  onWalk,
  onWelcome,
  setConnection,
} from '@game/session/sync';
import type { Intent } from '@game/types';
import { onLifecycle } from '@platform/lifecycle';

import { ApiFailure, joinCrew } from './api';
import type { Auth } from './auth';
import { liveUrl } from './url';

const log = logger('net');

const PING_EVERY_MS = 30_000;
const PRESENCE_MOVING_MS = 90;
const PRESENCE_STILL_MS = 5_000;
const BACKOFF_MS = [1_000, 2_000, 4_000, 8_000, 15_000];
/** The room closes with this when its world is gone for good (wiped): no point coming back. */
const CLOSE_GONE = 4003;
/** What a guest's join again can answer that means there's no way back in. */
const GONE: ReadonlySet<string> = new Set(['unknown-crew', 'crew-full']);

export function startConnection(game: Game, auth: Auth): () => void {
  let socket: WebSocket | null = null;
  let attempt = 0;
  let reconnectTimer = 0;
  let pingTimer = 0;
  let presenceTimer = 0;
  let lastPresenceAt = -Infinity;
  let lastPresence = '';
  let stopped = false;
  const p = vec();
  const h = vec();

  const send = (message: ClientMessage): boolean => {
    if (socket?.readyState !== WebSocket.OPEN) return false;
    socket.send(JSON.stringify(message));
    return true;
  };

  const hello = (): void => {
    send({
      v: PROTOCOL_VERSION,
      type: 'hello',
      seq: game.store.get().seq,
      rules: game.rulesVersion,
    });
  };

  const ping = (): void => {
    send({ v: PROTOCOL_VERSION, type: 'ping', t: game.deps.now() });
  };

  const sendIntent = (intent: Intent): void => {
    if (intent.kind === 'action')
      send({ v: PROTOCOL_VERSION, type: 'intent', id: intent.id, action: intent.action });
    else send({ v: PROTOCOL_VERSION, type: 'dev', id: intent.id, command: intent.command });
  };

  const presence = (): void => {
    const s = game.store.get();
    if (s.connection !== 'online' || !s.view) return;
    const now = game.deps.now();
    const moving = game.local.moving > 0.2;
    const since = now - lastPresenceAt;
    myPoint(game.local, p);
    myHeading(game.local, h);
    const message: ClientMessage = {
      v: PROTOCOL_VERSION,
      type: 'presence',
      p: [r4(p[0] ?? 0), r4(p[1] ?? 0), r4(p[2] ?? 0)],
      h: [r4(h[0] ?? 0), r4(h[1] ?? 0), r4(h[2] ?? 0)],
      moving,
    };
    const encoded = JSON.stringify(message);
    const changed = encoded !== lastPresence;
    if (!(changed && since >= PRESENCE_MOVING_MS) && since < PRESENCE_STILL_MS) return;
    if (socket?.readyState !== WebSocket.OPEN) return;
    socket.send(encoded);
    lastPresence = encoded;
    lastPresenceAt = now;
  };

  const close = (): void => {
    clearTimeout(reconnectTimer);
    clearInterval(pingTimer);
    clearInterval(presenceTimer);
    if (socket) {
      socket.onclose = null;
      socket.onmessage = null;
      socket.onopen = null;
      socket.onerror = null;
      socket.close();
      socket = null;
    }
  };

  const scheduleReconnect = (): void => {
    const s = game.store.get();
    if (stopped || !s.world || s.connection === 'outdated') return;
    const wait = BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)] ?? 15_000;
    attempt++;
    clearTimeout(reconnectTimer);
    reconnectTimer = window.setTimeout(connect, wait);
  };

  const connect = (): void => {
    close();
    const s = game.store.get();
    if (stopped || !s.world || !s.me || s.connection === 'outdated') return;
    setConnection(game, 'connecting');
    const worldId = s.world.id;
    // a guest away for long is no longer a member: join again first (harmless while still one)
    const rejoin = attempt > 0 && isGuest(s.me.id) ? joinCrew(worldId) : Promise.resolve();
    rejoin
      .then(() => auth.accessToken(attempt > 0))
      .then((token) => {
        if (stopped || game.store.get().world?.id !== worldId) return;
        if (!token) {
          setConnection(game, 'offline');
          return;
        }
        open(worldId, token);
      })
      .catch((error: unknown) => {
        if (error instanceof ApiFailure && GONE.has(error.code)) {
          game.events.emit('gone', {
            world: worldId,
            reason: error.code === 'crew-full' ? 'crew-full' : 'crew-gone',
          });
          return;
        }
        setConnection(game, 'offline');
        scheduleReconnect();
      });
  };

  const open = (worldId: string, token: string): void => {
    const ws = new WebSocket(liveUrl(worldId, token));
    socket = ws;
    ws.onopen = () => {
      hello();
      ping();
      pingTimer = window.setInterval(ping, PING_EVERY_MS);
      presenceTimer = window.setInterval(presence, PRESENCE_MOVING_MS);
      lastPresenceAt = -Infinity;
      lastPresence = '';
    };
    ws.onmessage = (event) => {
      let json: unknown;
      try {
        json = JSON.parse(String(event.data));
      } catch {
        log.warn('unreadable message');
        return;
      }
      const parsed = parseServerMessage(json);
      if (!parsed.ok) {
        log.warn('unknown message', parsed.error);
        return;
      }
      const msg = parsed.message;
      switch (msg.type) {
        case 'welcome':
          attempt = 0;
          onWelcome(game, msg);
          break;
        case 'event':
          onEvent(game, msg);
          break;
        case 'refused':
          onRefused(game, msg);
          break;
        case 'member':
          onMember(game, msg);
          break;
        case 'presence':
          onPresence(game, msg);
          break;
        case 'walk':
          onWalk(game, msg);
          break;
        case 'left':
          onLeft(game, msg);
          break;
        case 'pong':
          game.clock.onPong(msg.time, msg.t);
          break;
        case 'outdated':
          onOutdated(game);
          close();
          break;
      }
    };
    ws.onclose = (event) => {
      log.info('closed', event.code, event.reason);
      close();
      if (event.code === CLOSE_GONE) {
        game.events.emit('gone', { world: worldId, reason: 'crew-gone' });
        return;
      }
      if (game.store.get().connection !== 'outdated') setConnection(game, 'offline');
      scheduleReconnect();
    };
    ws.onerror = () => {
      /* onclose follows */
    };
  };

  const offCrew = game.events.on('world', (world) => {
    attempt = 0;
    if (world) connect();
    else {
      close();
      setConnection(game, 'offline');
    }
  });
  const offIntent = game.events.on('intent', sendIntent);
  const offWelcome = game.events.on('welcome', ({ pending }) => {
    for (const intent of pending) sendIntent(intent);
  });
  const offResync = game.events.on('resync', hello);
  const offWalk = game.events.on('walk', ({ to, start, duration }) => {
    send({ v: PROTOCOL_VERSION, type: 'walk', to, start, duration });
  });
  const offLifecycle = onLifecycle((active) => {
    if (!active) return;
    if (socket?.readyState === WebSocket.OPEN) ping();
    else if (game.store.get().world) {
      attempt = 0;
      connect();
    }
  });

  return () => {
    stopped = true;
    close();
    offCrew();
    offIntent();
    offWelcome();
    offResync();
    offWalk();
    offLifecycle();
  };
}
